<style scoped lang="scss">
form {
    h2 {
        color: #000;
    }
    ::v-deep(.formkit-select-icon) {
        height: 50px;
        margin: 0;
        padding: 0;
    }
}
main .container .card {
    background-color: rgba(91, 91, 98, 0.08);
}
</style>

<template>
    <div class="mb-5">
        <h1 class="page-title">{{ title }}</h1>
    </div>
    <div class="max-w-screen-xl mx-auto">
        <div class="flex flex-wrap -mx-4">
            <div class="w-full lg:w-1/4 px-3">
                <div class="mb-5">
                    <div class="card w-75 shadow-xl">
                        <div class="card-body items-center text-center">
                            <h5 class="card-title">Image*</h5>
                            <UploadImage :image-preview-url="getBaseUrl() + '/media/blank2.png'"
                                         @image-removed="onImageRemoved"
                                         @imageUploaded="onImageUpload"
                                         @error="onImageUploadError"
                            />
                        </div>
                    </div>
                </div>
                <div class="mb-5">
                    <div class="card w-75 shadow-xl">
                        <div class="card-body items-center text-center">
                            <h5 class="card-title">Icone 3D*</h5>
                            <UploadImage :image-preview-url="getBaseUrl() + '/media/blank2.png'"
                                         @image-removed="onImageIconRemoved"
                                         @imageUploaded="onImageIconUpload"
                                         @error="onImageUploadError"
                            />
                        </div>
                    </div>
                </div>
            </div>
        <div class="w-full lg:w-3/4 px-4">
        <div class="card w-full shadow-xl">
        <div class="card-body">
            <div class="w-full">
                <FormKit type="form"
                 :actions="false"
                 @submit="onSubmit"
                 #default="{ state: { valid } }"
                 outer-class="w-full"
                 incomplete-message="Veuillez remplir tous les champs correctement"
                >
                <FormKit type="text"
                 name="title"
                 label="Titre *"
                 validation="required|contains_alpha|matches:/^[^/]*$/"
                 :validation-messages="{
                    required: 'Ce champ est obligatoire.',
                    contains_alpha: 'Ce champ doit contenir des lettres..',
                    matches: 'Ce champ ne peut contenir de “/”.'
                 }"
                />
                <FormKit type="number"
                 name="position"
                 label="Position *"
                 validation="required|min:1"
                 :validation-messages="{
                    required: 'Ce champ est obligatoire.',
                    min: 'Ce champ doit être un nombre décimal superieur à 0',
                }"
                />
                <FormKit type="text"
                         name="reference"
                         label="Identifiant *"
                         validation="required"
                         :validation-messages="{
                            required: 'Ce champ est obligatoire.'
                         }"
                />
                <FormKit
                    v-if="type == 'region' && shouldShowAdditionalInfo"
                    type="text"
                    name="additionalInfo"
                    label="Références numériques *"
                    validation="required"
                    :validation-messages="{
                        required: 'Ce champ est obligatoire.',
                       }"
                />
                <p class="text-sm text-gray-500 mt-1 mb-2" v-if="type == 'region' && shouldShowAdditionalInfo">
                    Format attendu : nombres à 2 chiffres, séparés par des virgules. Exemple : <strong>12, 34, 56</strong>
                </p>
                <div class="card-actions">
                    <div v-if="loading === false">
                        <FormKit type="submit"
                         :label="submitBtnLabel"
                         :disabled="!valid"
                         :class="{ 'btn-primary': true, 'btn-block': true }"
                        />
                    </div>
                    <FormKit type="button"
                     label="Annuler"
                     @click="onCancel"
                     :class="{ 'btn-secondary': true, 'btn-block': true }"
                    />
                    </div>
                </FormKit>
            </div>
        </div>
        </div>
        </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {ref, computed, onMounted} from 'vue';
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import UploadImage from "@/components/forms/UploadImage.vue";
import {IMiddleMenu} from "@/models/homepage/IMiddleMenu";
import { useRoute, useRouter } from 'vue-router'

const title = ref<string>("Ajouter une référence");
const submitBtnLabel = 'Ajouter';
const router = useRouter();
const route = useRoute();
const loading = ref<boolean>(false);
const form = ref<IMiddleMenu>({
    image: null,
    icon3d: null
});
const configMoney = ref<{ locale?: string }>({})

const allowedTypes = ['region']

const type = computed(() => {
    const current = route.params.type as string | undefined
    return allowedTypes.includes(current ?? '') ? current : null
})

if (route.params.type && !allowedTypes.includes(route.params.type as string)) {
    router.replace({ name: 'addMiddleMenu' }) 
}

onMounted(async () => {
    try {
        const { data } = await ApiService.get('/config-money')
        configMoney.value = data.data
    } catch (e) {
        console.error('Erreur récupération config-money', e)
    }
})

const shouldShowAdditionalInfo = computed(() => {
    const value = (configMoney.value?.locale ?? 'fr_FR').toLowerCase()
    return !value.includes('en') ?? true
})

const onSubmit = async (formData) => {
    try {
        const payload = {
            position: parseInt(formData.position),
            title: formData.title,
            image: form.value.image?.uuid ?`/api/images/${form.value.image.uuid}` : null,
            icon3d: form.value.icon3d?.uuid ? `/api/images/${form.value.icon3d.uuid}` : null,
            blockType: type.value && type.value === "region" ? "region" : "current_offer",
            displayVersion: type.value && type.value === "region" ? "new" : "both",
            additionalInfo: formData.additionalInfo,
            reference: formData.reference
        }
        swal.confirm(`le champ "Idenfiant: ${formData.reference ?? ''}" ne pourra plus être modifié, êtes sûr(e) de la valeur renseignée ?`,
            "Annuler",
            submitBtnLabel
        )
            .then((results) => results.isConfirmed && addMiddleMenu(payload))
    } catch (err: any) {
        swal.error(err.errors[0]);
    }
}

const addMiddleMenu = (data) => {
    ApiService.post('middle_menus', data)
        .then(() => {
            loading.value = false;
            swal.info("Ajout avec succès")
                .then((result) => {
                    if (result.isConfirmed) {
                        if (type.value && type.value == "region") {
                            router.push({
                                name: "regionList"
                            });
                        } else {
                            router.push({
                                name: "middleMenuList"
                            });
                        }
                    }
                });
        })
        .catch(() => {
            swal.error("Une erreur est survenue lors de la modification");
        });
}

const onCancel = () => {
    swal.confirm(
        "Êtes-vous sûr de vouloir annuler l'ajout ?",
        "Oui",
        "Non"
    ).then((result) => {
        if (result.isDismissed) {
            if (type.value && type.value == "region") {
                router.push({
                    name: "regionList"
                });
            } else {
                router.push({
                    name: "middleMenuList"
                });
            }
        }
    });
}

const onImageUpload = (image) => {
    image ? form.value.image = image : form.value.image = null;
}

const onImageIconUpload = (image) => {
    image ? form.value.icon3d = image : form.value.icon3d = null;
}

const onImageRemoved = () => {
    form.value.image = null;
}

const onImageIconRemoved = () => {
    form.value.icon3d = null;
}

const onImageUploadError = (errorMsg: string) => {
    swal.error(errorMsg);
}

function getBaseUrl() {
    return window.location.origin;
}
</script>